/**
 * AddonAuditTab - Tab de histórico de auditoria
 * Exibe logs de todas as ações manuais realizadas com filtros avançados
 * 
 * @version 1.1
 * @date 2025-12-19
 * @changelog
 *   - v1.1: Adicionados filtros por período e tipo de ação
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  ChevronDown, 
  ChevronRight,
  Plus,
  RefreshCw,
  XCircle,
  Edit,
  RotateCcw,
  Filter,
  X
} from 'lucide-react';
import { useAddonManagement, AddonAuditLog } from '@/hooks/useAddonManagement';
import { format, isAfter, isBefore, parseISO, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Mapa de ícones e labels por tipo de ação
 */
const actionTypeConfig: Record<string, { icon: React.ElementType; label: string; color: string }> = {
  create_manual: { icon: Plus, label: 'Cadastro Manual', color: 'text-emerald-500' },
  sync_stripe: { icon: RefreshCw, label: 'Sincronização Stripe', color: 'text-blue-500' },
  cancel: { icon: XCircle, label: 'Cancelamento', color: 'text-red-500' },
  update: { icon: Edit, label: 'Atualização', color: 'text-amber-500' },
  reactivate: { icon: RotateCcw, label: 'Reativação', color: 'text-purple-500' },
  adjust_quantity: { icon: Edit, label: 'Ajuste de Quantidade', color: 'text-orange-500' },
};

/**
 * Opções de tipo de ação para o filtro
 */
const ACTION_TYPE_OPTIONS = [
  { value: 'all', label: 'Todas as Ações' },
  { value: 'create_manual', label: 'Cadastro Manual' },
  { value: 'sync_stripe', label: 'Sincronização Stripe' },
  { value: 'cancel', label: 'Cancelamento' },
  { value: 'update', label: 'Atualização' },
  { value: 'reactivate', label: 'Reativação' },
  { value: 'adjust_quantity', label: 'Ajuste de Quantidade' },
];

/**
 * Componente para exibir linha de log com expansão
 */
function AuditLogRow({ log }: { log: AddonAuditLog }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const config = actionTypeConfig[log.action_type] || { 
    icon: Edit, 
    label: log.action_type, 
    color: 'text-muted-foreground' 
  };
  const Icon = config.icon;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <TableRow className="group">
        <TableCell>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${config.color}`} />
            <span>{config.label}</span>
          </div>
        </TableCell>
        <TableCell className="font-medium">
          {log.tenant?.name || '-'}
        </TableCell>
        <TableCell>
          {log.plan_addons?.name || '-'}
        </TableCell>
        <TableCell>
          {log.notes ? (
            <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
              {log.notes}
            </span>
          ) : '-'}
        </TableCell>
      </TableRow>
      
      <CollapsibleContent asChild>
        <TableRow className="bg-muted/30">
          <TableCell colSpan={6} className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {/* Dados novos */}
              {log.new_data && Object.keys(log.new_data).length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 text-emerald-600">Dados Criados/Novos:</h4>
                  <pre className="bg-background p-3 rounded-md overflow-auto text-xs">
                    {JSON.stringify(log.new_data, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* Dados antigos */}
              {log.old_data && Object.keys(log.old_data).length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 text-red-600">Dados Anteriores:</h4>
                  <pre className="bg-background p-3 rounded-md overflow-auto text-xs">
                    {JSON.stringify(log.old_data, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* Metadados */}
              <div className="md:col-span-2 flex gap-4 text-muted-foreground">
                {log.stripe_subscription_id && (
                  <span>Stripe: {log.stripe_subscription_id}</span>
                )}
                {log.ip_address && (
                  <span>IP: {log.ip_address}</span>
                )}
                <span>ID: {log.id.slice(0, 8)}...</span>
              </div>
            </div>
          </TableCell>
        </TableRow>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function AddonAuditTab() {
  const { t } = useTranslation('admin');
  const { auditLogs, isLoadingAuditLogs } = useAddonManagement();
  
  // Estados de filtro
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Filtra logs baseado nos filtros aplicados
  const filteredLogs = useMemo(() => {
    return auditLogs.filter(log => {
      // Filtro por tipo de ação
      if (actionTypeFilter !== 'all' && log.action_type !== actionTypeFilter) {
        return false;
      }

      // Filtro por data início
      if (startDate) {
        const logDate = parseISO(log.created_at);
        const filterStart = startOfDay(parseISO(startDate));
        if (isBefore(logDate, filterStart)) {
          return false;
        }
      }

      // Filtro por data fim
      if (endDate) {
        const logDate = parseISO(log.created_at);
        const filterEnd = endOfDay(parseISO(endDate));
        if (isAfter(logDate, filterEnd)) {
          return false;
        }
      }

      return true;
    });
  }, [auditLogs, actionTypeFilter, startDate, endDate]);

  // Verifica se há filtros ativos
  const hasActiveFilters = actionTypeFilter !== 'all' || startDate || endDate;

  // Limpa todos os filtros
  const clearFilters = () => {
    setActionTypeFilter('all');
    setStartDate('');
    setEndDate('');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            {t('addonManagement.audit.title', 'Histórico de Ações')}
          </CardTitle>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Limpar
              </Button>
            )}
            <Button 
              variant={showFilters ? 'secondary' : 'outline'} 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
              {hasActiveFilters && (
                <Badge variant="default" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  !
                </Badge>
              )}
            </Button>
          </div>
        </div>
        
        {/* Painel de filtros */}
        {showFilters && (
          <div className="mt-4 p-4 border rounded-lg bg-muted/30 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Filtro por tipo de ação */}
              <div className="space-y-2">
                <Label htmlFor="actionType">Tipo de Ação</Label>
                <Select value={actionTypeFilter} onValueChange={setActionTypeFilter}>
                  <SelectTrigger id="actionType">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_TYPE_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Data início */}
              <div className="space-y-2">
                <Label htmlFor="startDate">Data Início</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Data fim */}
              <div className="space-y-2">
                <Label htmlFor="endDate">Data Fim</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead className="w-40">Data/Hora</TableHead>
                <TableHead>Ação</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Add-on</TableHead>
                <TableHead>Observações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingAuditLogs ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Carregando histórico...
                  </TableCell>
                </TableRow>
              ) : filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    {hasActiveFilters 
                      ? 'Nenhuma ação encontrada com os filtros aplicados'
                      : 'Nenhuma ação registrada ainda'
                    }
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <AuditLogRow key={log.id} log={log} />
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Contador */}
        <div className="text-sm text-muted-foreground mt-4">
          {hasActiveFilters 
            ? `Exibindo ${filteredLogs.length} de ${auditLogs.length} registros`
            : `Exibindo ${auditLogs.length} registros mais recentes`
          }
        </div>
      </CardContent>
    </Card>
  );
}
